import type { FC } from 'react';
import { useState } from 'react';
import { Grid2, Box } from '@mui/material';
import Image from 'next/image';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import { useTranslations } from 'next-intl';
import ISOImage from '/public/images/authorization/iso.svg';
import SOCImage from '/public/images/authorization/soc.svg';
import GDPRImage from '/public/images/authorization/gdpr.svg';
import CountryCountImage from '/public/images/instructions/countryCount.svg';
import CostSavingsImage from '/public/images/instructions/costSavings.svg';
import TimeImage from '/public/images/instructions/time.svg';

const AuthList = [
  {
    src: ISOImage,
    value: 'iso',
    alt: 'iso image',
  },
  {
    src: SOCImage,
    value: 'soc',
    alt: 'sco image',
  },
  {
    src: GDPRImage,
    value: 'gdpr',
    alt: 'gdpr image',
  },
];

const SpecialityList = [
  {
    src: CountryCountImage,
    value: 'countryCount',
    start: 50,
    end: 100,
    prefix: '',
    suffix: '+',
    alt: 'Countries & Regions employment Image',
    tDescription: 'countriesDescription',
  },
  {
    src: CostSavingsImage,
    value: 'costSavings',
    start: 15,
    end: 30,
    prefix: '$',
    suffix: 'M',
    alt: 'In cost savings Image',
    tDescription: 'savingsDescription',
  },
  {
    src: TimeImage,
    value: 'time',
    start: 12,
    end: 24,
    prefix: '',
    suffix: '/7',
    alt: 'Customer-centered services Image',
    tDescription: 'supportDescription',
  },
];

const Authentication: FC = () => {
  const t = useTranslations('Home');
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [viewPortEntered, setViewPortEntered] = useState(false);
  const setViewPortVisibility = (isVisible) => {
    if (viewPortEntered) {
      return;
    }

    setViewPortEntered(isVisible);
  };

  return (
    <Grid2
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretched', sm: 'center' }}
      justifyContent={'center'}
      spacing={4}
      container
    >
      <Grid2
        size={{
          xs: 12,
          sm: 12,
        }}
        textAlign="center"
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff' }}>
          {t('honorTitle')}
        </Typography>
      </Grid2>
      <Grid2
        size={{
          xs: 12,
          sm: 12,
        }}
        textAlign="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {AuthList.map((item) => (
          <Box px={4} key={`${item.value}`}>
            <Image src={item.src} alt={item.alt} width={78} height={84} />
          </Box>
        ))}
      </Grid2>
      <Grid2
        size={12}
        textAlign="center"
        display="flex"
        alignItems="center"
        justifyContent="center"
        container
      >
        {SpecialityList.map((item) => (
          <Grid2
            size={{
              xs: 12,
              md: 4,
            }}
            key={`${item.value}`}
          >
            <Typography
              variant="h2"
              gutterBottom
              sx={{ color: '#fff' }}
              align="center"
            >
              <Box fontWeight={600}>
                <VisibilitySensor
                  onChange={(isVisible) => setViewPortVisibility(isVisible)}
                  delayedCall
                >
                  <CountUp
                    duration={3}
                    end={viewPortEntered ? (item.end as number) : 0}
                    start={item.start}
                    prefix={item.prefix}
                    suffix={item.suffix}
                  />
                </VisibilitySensor>
              </Box>
            </Typography>
            <Typography
              color="text.secondary"
              component="p"
              align="center"
              sx={{ color: '#fff' }}
            >
              {t(item.tDescription)}
            </Typography>
          </Grid2>
        ))}
      </Grid2>
    </Grid2>
  );
};

export default Authentication;
